import { ArrowDownRight, ArrowUpRight, Check, Info, Minus, Plus, X } from 'lucide-react'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'
import { cn } from '@/lib/cn'
import { Spark } from './charts'
import { healthBand, initials } from './util'

/*
 * Small pieces of the control panel redesign, ported from the prototype: KPI tiles, section
 * cards, chips, health bars and rings, the status strip, tabs, filter chips and notes.
 */

/**
 * The change against a previous figure: green when it moved the good way. `points` shows the
 * difference itself ("3 pt") for figures that are already percentages.
 */
export function Change({
  now,
  previous,
  invert = false,
  points = false,
}: {
  now: number | null | undefined
  previous: number | null | undefined
  invert?: boolean
  points?: boolean
}) {
  if (now == null || previous == null) return null
  const diff = now - previous
  if (!points && previous === 0) return null
  const rel = points ? diff : (diff / Math.abs(previous)) * 100
  const flat = points ? Math.abs(diff) < 0.05 : Math.abs(rel) < 0.5
  const good = invert ? diff < 0 : diff > 0
  const Icon = flat ? Minus : diff > 0 ? ArrowUpRight : ArrowDownRight
  const text = points
    ? `${Math.abs(Math.round(diff * 10) / 10)} pt`
    : `${Math.abs(rel).toFixed(Math.abs(rel) < 10 ? 1 : 0)}%`
  return (
    <span className={cn('delta', flat ? 'flat' : good ? 'up' : 'down')}>
      <Icon aria-hidden="true" />
      {text}
      <span className="sr-only">{flat ? ' no change' : good ? ' better' : ' worse'}</span>
    </span>
  )
}

/** A headline figure: label, value, change against the previous period, and a trend line. */
export function KpiTile({
  label,
  value,
  unit,
  now,
  previous,
  invert,
  points,
  compare,
  spark,
  sparkColor,
  to,
  hint,
}: {
  label: string
  value: ReactNode
  unit?: string
  now?: number | null
  previous?: number | null
  invert?: boolean
  points?: boolean
  /** What it's compared with, or a short note when there's nothing to compare. */
  compare?: string
  spark?: number[]
  sparkColor?: string
  to?: string
  hint?: string
}) {
  const body = (
    <>
      <span className="l">
        <span>{label}</span>
        {hint && (
          <span title={hint}>
            <Info className="size-3.5" aria-label={hint} />
          </span>
        )}
      </span>
      <span className="v">
        {value}
        {unit && <small> {unit}</small>}
      </span>
      <span className="cmp">
        <Change now={now} previous={previous} invert={invert} points={points} />
        {compare && <span>{compare}</span>}
      </span>
      {spark && spark.length > 1 && <Spark values={spark} color={sparkColor} />}
    </>
  )
  return to ? (
    <Link to={to} className="mt">
      {body}
    </Link>
  ) : (
    <div className="mt" style={{ cursor: 'default' }}>
      {body}
    </div>
  )
}

/** A card with a title, a short line under it and optional actions on the right. */
export function Panel({
  title,
  sub,
  extra,
  children,
  className,
}: {
  title: ReactNode
  sub?: ReactNode
  extra?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <section className={cn('card pad sec', className)}>
      <div className="ch">
        <div>
          <h3>{title}</h3>
          {sub && <p>{sub}</p>}
        </div>
        {extra}
      </div>
      {children}
    </section>
  )
}

export function SectionHead({
  title,
  small,
  extra,
}: {
  title: ReactNode
  small?: ReactNode
  extra?: ReactNode
}) {
  return (
    <div className="sec-h">
      <h2>
        {title}
        {small && <small>{small}</small>}
      </h2>
      {extra}
    </div>
  )
}

export type ChipTone = 'ok' | 'warn' | 'bad' | 'lime' | 'ink' | 'line' | ''

export function Chip({
  tone = '',
  children,
  className,
}: {
  tone?: ChipTone
  children: ReactNode
  className?: string
}) {
  return <span className={cn('chip', tone, className)}>{children}</span>
}

const planClass = (key: string) =>
  key.startsWith('lifetime')
    ? 'lifetime'
    : key === 'free'
      ? 'free'
      : key === 'starter'
        ? 'starter'
        : key === 'business' || key === 'agency'
          ? 'agency'
          : 'pro'

/** The plan with its colour square: grey Free, rust Starter, olive Professional, plum Business; a round ink dot for lifetime plans. */
export function PlanTag({ plan, name, yearly }: { plan: string; name: string; yearly?: boolean }) {
  return (
    <span className={cn('plan', planClass(plan))}>
      <i />
      {name}
      {yearly && <span style={{ fontWeight: 500, color: 'var(--subtle)' }}> · yearly</span>}
    </span>
  )
}

export function CountryCode({ code, name }: { code: string; name?: string }) {
  return (
    <span className="cc" title={name ?? code}>
      {code}
    </span>
  )
}

const AV = ['av-1', 'av-2', 'av-3', 'av-4']

/** A person's initials on a colour picked from their name, or a dashed circle for nobody. */
export function Avatar({
  name,
  size = '',
  me = false,
}: {
  name: string | null | undefined
  size?: '' | 'xs' | 'sm' | 'lg'
  me?: boolean
}) {
  if (!name) {
    return (
      <span className={cn('av none', size)} title="Nobody yet">
        <span className="sr-only">Nobody</span>
      </span>
    )
  }
  let sum = 0
  for (let i = 0; i < name.length; i++) sum += name.charCodeAt(i)
  const tone = me ? 'av-me' : AV[sum % AV.length]
  return (
    <span className={cn('av', size, tone)} title={name}>
      {initials(name)}
    </span>
  )
}

/** A company's first letter in a tile. */
export function Monogram({ name, size }: { name: string; size?: 'sm' | 'lg' }) {
  const style = size === 'sm' ? { width: 30, height: 30, fontSize: 13, borderRadius: 9 } : undefined
  return (
    <span className={cn('mono-t', size === 'lg' && 'lg')} style={style} aria-hidden="true">
      {name.trim().charAt(0).toUpperCase() || '?'}
    </span>
  )
}

export function HealthBar({ value }: { value: number }) {
  return (
    <span className="health" title={`Health ${value} of 100`}>
      <span className="tr">
        <span className={cn('fl', healthBand(value))} style={{ width: `${value}%` }} />
      </span>
      {value}
    </span>
  )
}

export function HealthRing({ value }: { value: number }) {
  const r = 32
  const c = 2 * Math.PI * r
  const col = value >= 70 ? 'var(--lime-hover)' : value >= 45 ? 'var(--warn-dot)' : 'var(--bad-dot)'
  return (
    <div className="hring" title={`Health ${value} of 100`}>
      <svg viewBox="0 0 76 76" aria-hidden="true">
        <circle cx={38} cy={38} r={r} fill="none" stroke="var(--sunken)" strokeWidth={8} />
        <circle
          cx={38}
          cy={38}
          r={r}
          fill="none"
          stroke={col}
          strokeWidth={8}
          strokeLinecap="round"
          strokeDasharray={`${(value / 100) * c} ${c}`}
        />
      </svg>
      <b>{value}</b>
      <small>HEALTH</small>
    </div>
  )
}

/** A meter for a usage or a share: amber from 70%, red from 85% when `strict`. */
export function Track({ ratio, tone }: { ratio: number; tone?: 'warn' | 'bad' | '' }) {
  return (
    <span className="track">
      <span className={tone} style={{ width: `${Math.max(0, Math.min(1, ratio)) * 100}%` }} />
    </span>
  )
}

/** A strip of statuses with counts; picking one filters the page. */
export function Pipe<T extends string>({
  items,
  value,
  onChange,
}: {
  items: { key: T; label: string; count: number | string; dot?: string; sub?: string }[]
  value?: T | null
  onChange?: (key: T | null) => void
}) {
  return (
    <div className="pipe">
      {items.map((i) => (
        <button
          key={i.key}
          type="button"
          aria-pressed={value === i.key}
          onClick={() => onChange?.(value === i.key ? null : i.key)}
        >
          <span className="k">
            <span className={cn('dot', i.dot)} />
            {i.label}
          </span>
          <span className="v">{i.count}</span>
          {i.sub && <span className="s">{i.sub}</span>}
        </button>
      ))}
    </div>
  )
}

export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
  label,
}: {
  tabs: { key: T; label: string; count?: number | string; alert?: boolean }[]
  value: T
  onChange: (key: T) => void
  label: string
}) {
  return (
    <div className="tabs" role="tablist" aria-label={label}>
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          role="tab"
          className="tab"
          aria-selected={value === t.key}
          onClick={() => onChange(t.key)}
        >
          {t.label}
          {t.count !== undefined && (
            <span
              className="c"
              style={t.alert ? { background: 'var(--bad-soft)', color: 'var(--bad)' } : undefined}
            >
              {t.count}
            </span>
          )}
        </button>
      ))}
    </div>
  )
}

/** A filter pill: dashed with a plus when unset, filled with its value when set, a popover to choose. */
export function FilterChip({
  label,
  icon,
  value,
  options,
  onChange,
}: {
  label: string
  icon?: ReactNode
  value: string | null | undefined
  options: { value: string; label: string; count?: number | string }[]
  onChange: (value: string | null) => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLSpanElement>(null)
  useEffect(() => {
    if (!open) return
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', esc)
    }
  }, [open])
  const current = options.find((o) => o.value === value)
  return (
    <span className="anchor" ref={ref}>
      <button
        type="button"
        className={cn('fchip', current && 'on')}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        {current ? icon : <Plus aria-hidden="true" />}
        {label}
        {current && (
          <>
            : <span className="v">{current.label}</span>
            <span
              className="x"
              role="button"
              tabIndex={0}
              aria-label={`Clear ${label}`}
              onClick={(e) => {
                e.stopPropagation()
                onChange(null)
                setOpen(false)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  e.stopPropagation()
                  onChange(null)
                }
              }}
            >
              <X aria-hidden="true" />
            </span>
          </>
        )}
      </button>
      {open && (
        <div className="pop" role="listbox" aria-label={label}>
          <div className="pop-l">{label}</div>
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              role="option"
              aria-selected={o.value === value}
              className="opt"
              onClick={() => {
                onChange(o.value === value ? null : o.value)
                setOpen(false)
              }}
            >
              <span className={cn('cbx', o.value === value && 'on')}>
                <Check aria-hidden="true" />
              </span>
              {o.label}
              {o.count !== undefined && <span className="cnt">{o.count}</span>}
            </button>
          ))}
        </div>
      )}
    </span>
  )
}

/** A short notice in the page: amber by default, quiet with `calm`. */
export function Note({
  icon,
  title,
  children,
  calm = false,
}: {
  icon?: ReactNode
  title: ReactNode
  children?: ReactNode
  calm?: boolean
}) {
  return (
    <div className={cn('note', calm && 'calm2')}>
      {icon}
      <div>
        <b>{title}</b>
        {children && <p>{children}</p>}
      </div>
    </div>
  )
}

/** One line in an activity feed. */
export function FeedItem({
  icon,
  tone = '',
  children,
  meta,
  when,
}: {
  icon: ReactNode
  tone?: string
  children: ReactNode
  meta?: ReactNode
  when?: ReactNode
}) {
  return (
    <div className="fe">
      <span className={cn('fe-ic', tone)}>{icon}</span>
      <div className="fe-t">
        {children}
        {meta && <small>{meta}</small>}
      </div>
      {when && <span className="fe-time">{when}</span>}
    </div>
  )
}
